import BaseItemId from 'owa-service/lib/contract/BaseItemId';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import EventScope from 'owa-service/lib/contract/EventScope';
import strings from 'owa-strings/owa-calendar-strings';
import { action } from 'satcheljs';
import { addDatapointConfig } from 'owa-analytics-actions';
import { ActionDatapoint } from 'owa-analytics';
import { CalendarEventsLoadingState } from '../types/CalendarEventsLoadingState';
import { CalendarId } from 'owa-calendar-cache';
import { ClientItemId } from 'owa-client-ids';
import { DateRange } from 'owa-datetime-utils';

export const calendarEventsRemoved = action(
    'calendarEventsRemoved',
    (eventsRemoved: CalendarEvent[]) => ({
        eventsRemoved,
    })
);

export const calendarEventsAdded = action(
    'calendarEventsAdded',
    (eventsAdded: CalendarEvent[]) => ({
        eventsAdded,
    })
);

export const calendarEventUpdated = action(
    'calendarEventUpdated',
    (eventUpdated: CalendarEvent) => ({
        eventUpdated,
    })
);

export const cacheReloaded = action(
    'cacheReloaded',
    (previousEvents: CalendarEvent[], reloadedEvents: CalendarEvent[]) => ({
        previousEvents,
        reloadedEvents,
    })
);

export const deleteEventsCache = action('deleteEventsCache', (folderId: string) => ({ folderId }));

export const loadingCalendarEvents = action(
    'loadingCalendarEvents',
    (calendarId: CalendarId, loadingState: CalendarEventsLoadingState) =>
        addDatapointConfig(
            {
                name: 'CalendarLoadState',
                customData: {
                    loadingState,
                },
                options: {
                    isVerbose: true,
                },
            },
            {
                calendarId,
                loadingState,
            }
        )
);

/**
 * Calendar item operation result
 */
export interface CalendarEventOperationResult {
    isSuccessful: boolean;
}

/**
 * Create operation result
 */
export interface CalendarEventCreateResult extends CalendarEventOperationResult {
    createdItem: CalendarEvent;
}

/**
 * It creates a calendar event locally (local lie) while also sending a request to create
 * the event.
 * @param eventToCreate The event that should be created.
 * @param rangeToCreate The range any fake items should be constrained to
 * @param actionToExecute The action that will be invoked to create calendar event at backend.
 * @param onEventCreateSuccess Will be called when the event is created successfully at the backend using `actionToExecute`. The created event will be passed to it as param.
 * @param onEventCreatedFailed Will be called if there is an error in creating calendar event.
 * @param errorMessage The error message to show in case of failue.
 */
export const instantCalendarEventCreate = action(
    'instantCalendarEventCreate',
    <T extends CalendarEventCreateResult>(
        eventToCreate: CalendarEvent,
        rangeToCreate: DateRange,
        actionToExecute: (eventToCreate: CalendarEvent) => Promise<T>,
        onEventCreatedSuccess: (instantEvent: CalendarEvent) => void,
        onEventCreatedFailed: (error: any) => void = () => {},
        errorMessage: string = strings.calendarNotificationsBarCreateEventFailed
    ) => ({
        eventToCreate,
        rangeToCreate,
        actionToExecute,
        errorMessage,
        onEventCreatedSuccess,
        onEventCreatedFailed,
    })
);

/**
 * It updates the calendar event locally (local lie) while also sending a request to update
 * the event.
 * @param eventId The id of the event to update.
 * @param eventBeforeUpdate The state of the event before it was updated.
 * @param eventChanges The changes in the event that need to be updated.
 * @param updateSeries Whether the series should be updated or not.
 * @param rangeToCreate The range any fake items should be constrained to
 * @param actionToExecute The action that will be invoked to update calendar event at backend.
 * @param onEventUpdateSuccess Will be called when the event is updated successfully at the backend using `actionToExecute`.
 * @param onEventUpdateFailed Will be called if there is an error in updating calendar event.
 * @param errorMessage The error message to show in case of failue.
 * @param showNotification Whether to show a saved notification. Defaults to false.
 */
export const instantCalendarEventUpdate = action(
    'instantCalendarEventUpdate',
    <T extends CalendarEventOperationResult>(
        eventId: ClientItemId,
        eventChanges: CalendarEvent,
        eventScope: EventScope,
        rangeToCreate: DateRange,
        actionToExecute: (idToUpdate: BaseItemId) => Promise<T>,
        onEventUpdateSuccess: () => void,
        onEventUpdateFailed: (error: any) => void = () => {},
        errorMessage: string = strings.calendarNotificationsBarUpdateEventFailed,
        showNotification: boolean = false
    ) => ({
        eventId,
        eventChanges,
        eventScope,
        rangeToCreate,
        actionToExecute,
        onEventUpdateSuccess,
        onEventUpdateFailed,
        errorMessage,
        showNotification,
    })
);

/**
 * It deletes the calendar event locally (local lie) while also sending a request to delete
 * the event.
 * @param eventId The id of the event to delete.
 * @param eventToDelete The event that should be deleted.
 * @param deleteSeries Whether the series should be deleted or not.
 * @param actionDatapoint passed from previous action so that user perceived time can be marked
 * @param actionToExecute The action that will be invoked to delete calendar event at backend.
 * @param onEventDeleteSuccess Will be called when the event is deleted successfully at the backend using `actionToExecute`.
 * @param onEventDeleteFailed Will be called if there is an error in deleting calendar event.
 * @param errorMessage The error message to show in case of failue.
 * @param responseSent Whether or not the user chose to send a response to the organizer
 */
export const instantCalendarEventDelete = action(
    'instantCalendarEventDelete',
    <T extends CalendarEventOperationResult>(
        eventId: ClientItemId,
        eventScope: EventScope,
        dataPoint: ActionDatapoint,
        actionToExecute: (id: BaseItemId) => Promise<T>,
        onEventDeleteSuccess: () => void,
        onEventDeleteFailed: (error: any) => void = () => {},
        errorMessage: string = strings.calendarNotificationsBarDeleteEventFailed,
        responseSent: boolean = false
    ) => ({
        eventId,
        eventScope,
        dataPoint,
        actionToExecute,
        errorMessage,
        onEventDeleteSuccess,
        onEventDeleteFailed,
        responseSent,
    })
);

export const updatingCalendarEventFailed = action(
    'updatingCalendarEventFailed',
    (
        eventBeforeUpdate: CalendarEvent,
        errorMessage: string,
        allowLocalUpdate: boolean,
        retryFunction: () => void
    ) => ({
        eventBeforeUpdate,
        errorMessage,
        allowLocalUpdate,
        retryFunction,
    })
);
